// Wires the flag-gated session-telemetry recorder to auth/session state.
// Mounted once in RootNavigator (inside the redux Provider). The recorder
// itself is a plain singleton — this hook only tells it when to run and as
// whom; the id selectors mirror what errorReporting.sessionContext reads.
//
// Two flag sources, deliberately (ED-2570 follow-up #3):
//   - Signed in  → the authenticated featureFlags slice (authoritative).
//   - Signed out → the PUBLIC settings bag (/users/get-settings, loaded
//     pre-auth for the maintenance gate) — the featureFlags slice can't
//     exist yet. Both session_telemetry_enabled AND
//     session_telemetry_prelogin_enabled must be true to record anonymously.
//
// The awkward middle: right after sign-in the featureFlags fetch is still in
// flight (everything reads false). If an anonymous session is live we HOLD
// rather than stop, so the claim survives the gap; once `fetched` flips, the
// recorder either claims (flag on) or stops (flag genuinely off).

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFeatureFlag } from './useFeatureFlag';
import { recorder } from '../services/sessionTelemetry/recorder';

export function useSessionTelemetry(): void {
  // Dark-launched flags — default FALSE while the boot fetch is in flight so
  // we never record ahead of an explicit enable.
  const enabled = useFeatureFlag('session_telemetry_enabled', false);
  const replay = useFeatureFlag('session_replay_enabled', false);
  const flagsFetched = useSelector((s: any) => !!s?.featureFlags?.fetched);
  const accountId = useSelector((s: any) => s?.session?.account?.account_id);
  const cognitoId = useSelector((s: any) => s?.authentication?.user?.cognito_id);
  const preloginOn = useSelector(
    (s: any) =>
      !!s?.session?.settings?.session_telemetry_enabled &&
      !!s?.session?.settings?.session_telemetry_prelogin_enabled,
  );

  useEffect(() => {
    if (accountId && cognitoId) {
      if (enabled) {
        recorder.configure({ accountId: String(accountId), cognitoId: String(cognitoId), replay });
      } else if (flagsFetched) {
        recorder.stop('flag_off');
      } else if (!preloginOn) {
        recorder.stop('flags_pending');
      }
      // else: authenticated but flags still loading with an anonymous
      // session possibly live — hold, the next effect run resolves it.
    } else if (preloginOn) {
      recorder.configureAnonymous();
    } else {
      recorder.stop('signed_out');
    }
  }, [enabled, replay, flagsFetched, accountId, cognitoId, preloginOn]);

  // Unmount = app teardown on web; make sure the tail flushes.
  useEffect(() => () => recorder.stop('unmount'), []);
}
